/**
 * Token Management Utility
 * Handles JWT access tokens and refresh tokens
 * 
 * Security Strategy:
 * - Access Token: 2h expiry, stored in sessionStorage
 * - Refresh Token: 10d expiry, stored in httpOnly cookie (backend) AND sessionStorage (fallback)
 * - Automatic refresh before expiry
 */

import { getSecureItem, setSecureItem, removeSecureItem, StorageKeys } from './secureStorage';
import { apiInst } from '../hooks/apiHandler';

/**
 * Check if token is expired or about to expire
 * @param {string} token - JWT token
 * @param {number} bufferMinutes - Minutes before expiry to consider expired (default: 5)
 * @returns {boolean}
 */
export const isTokenExpired = (token, bufferMinutes = 5) => {
  if (!token) return true;

  try {
    // Decode JWT payload (base64)
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    if (!payload.exp) return true;

    // Check if token expires within buffer time
    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const bufferTime = bufferMinutes * 60 * 1000;
    const now = Date.now();

    return (expiryTime - now) <= bufferTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

/**
 * Get token expiry time in human-readable format
 * @param {string} token - JWT token
 * @returns {string|null}
 */
export const getTokenExpiry = (token) => {
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    if (!payload.exp) return null;

    const expiryDate = new Date(payload.exp * 1000);
    return expiryDate.toLocaleString();
  } catch (error) {
    return null;
  }
};

/**
 * Refresh the access token using refresh token
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export const refreshAccessToken = async () => {
  try {
    const userData = getSecureItem(StorageKeys.USER_DATA);
    
    if (!userData?.refreshToken) {
      console.warn('⚠️ No refresh token available');
      return null;
    }

    console.log('🔄 Refreshing access token...');

    // Call backend refresh endpoint
    const response = await apiInst.post('/auth/refresh-token', {
      refreshToken: userData.refreshToken
    });

    if (response.data?.code === 0 && response.data?.data?.jwt) {
      const newAccessToken = response.data.data.jwt;
      
      // Update stored user data with new access token
      const updatedUserData = {
        ...userData,
        jwt: newAccessToken,
        token: newAccessToken
      };
      
      setSecureItem(StorageKeys.USER_DATA, updatedUserData);
      
      console.log('✅ Access token refreshed successfully');
      return newAccessToken;
    } else {
      console.error('❌ Token refresh failed:', response.data?.msg);
      return null;
    }
  } catch (error) {
    console.error('❌ Error refreshing token:', error);
    return null;
  }
};

/**
 * Get current access token, refresh if needed
 * @param {boolean} autoRefresh - Automatically refresh if token is expired
 * @returns {Promise<string|null>}
 */
export const getAccessToken = async (autoRefresh = true) => {
  const userData = getSecureItem(StorageKeys.USER_DATA);
  
  if (!userData?.jwt) {
    return null;
  }

  // Check if token is expired or about to expire
  if (autoRefresh && isTokenExpired(userData.jwt)) {
    console.log('⏰ Token expiring soon, refreshing...');
    const newToken = await refreshAccessToken();
    return newToken || userData.jwt; // Fallback to old token if refresh fails
  }

  return userData.jwt;
};

/**
 * Store tokens securely
 * @param {object} tokenData - Object containing jwt, refreshToken, etc.
 */
export const storeTokens = (tokenData) => {
  const userData = {
    jwt: tokenData.jwt || tokenData.token,
    token: tokenData.token || tokenData.jwt,
    refreshToken: tokenData.refreshToken,
    userID: tokenData.userID,
    sAccess: tokenData.sAccess,
    email: tokenData.email,
    // Store token metadata
    _tokenExpiry: getTokenExpiry(tokenData.jwt || tokenData.token),
    _storedAt: new Date().toISOString()
  };

  setSecureItem(StorageKeys.USER_DATA, userData);
  
  console.log('✅ Tokens stored securely');
  console.log('📅 Access token expires:', userData._tokenExpiry);
};

/**
 * Clear all tokens (logout)
 */
export const clearTokens = () => {
  removeSecureItem(StorageKeys.USER_DATA);
  removeSecureItem(StorageKeys.DEVICE_ID);
  removeSecureItem(StorageKeys.DEVICE_ADMIN);
  console.log('🗑️ All tokens cleared');
};

/**
 * Setup automatic token refresh
 * Checks token every minute and refreshes if needed
 */
let refreshInterval = null;

export const startTokenRefreshTimer = () => {
  // Clear existing interval
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }

  // Check token every minute
  refreshInterval = setInterval(async () => {
    const userData = getSecureItem(StorageKeys.USER_DATA);
    
    if (!userData?.jwt) {
      clearInterval(refreshInterval);
      return;
    }

    // Refresh if token expires in next 5 minutes
    if (isTokenExpired(userData.jwt, 5)) {
      await refreshAccessToken();
    }
  }, 60000); // Check every minute

  console.log('⏱️ Token refresh timer started');
};

export const stopTokenRefreshTimer = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
    console.log('⏱️ Token refresh timer stopped');
  }
};

/**
 * Token info for debugging
 */
export const getTokenInfo = () => {
  const userData = getSecureItem(StorageKeys.USER_DATA);
  
  if (!userData?.jwt) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(userData.jwt.split('.')[1]));
    
    return {
      userID: payload.id,
      name: payload.name,
      issuedAt: new Date(payload.iat * 1000).toLocaleString(),
      expiresAt: new Date(payload.exp * 1000).toLocaleString(),
      isExpired: isTokenExpired(userData.jwt, 0),
      expiresIn: Math.round((payload.exp * 1000 - Date.now()) / 60000) + ' minutes',
      hasRefreshToken: !!userData.refreshToken
    };
  } catch (error) {
    return null;
  }
};

export default {
  isTokenExpired,
  getTokenExpiry,
  refreshAccessToken,
  getAccessToken,
  storeTokens,
  clearTokens,
  startTokenRefreshTimer,
  stopTokenRefreshTimer,
  getTokenInfo
};
